import {
  extractFontNames,
  extractHexColors,
  handoffSnapshotHasVisibleFields,
  looksLikeHandoffUrl,
  type HandoffSnapshotFieldKey,
  type HandoffSnapshotFieldView,
  type MarketplaceOrderHandoffDefaults,
  type MarketplaceOrderHandoffSnapshot,
} from "@nexus/contracts";

export const VENDOR_BRIEF_SHORT_VALUE_MAX = 40;
export const VENDOR_BRIEF_INSTRUCTIONS_CLAMP = 3;
export const VENDOR_BRIEF_LONG_CLAMP = 2;
export const VENDOR_BRIEF_LABEL_COL_PX = 160;
export const VENDOR_BRIEF_ROW_MIN_PX = 32;
export const VENDOR_BRIEF_RAIL_PX = 3;
export const VENDOR_BRIEF_COLLAPSED_BUDGET_PX = 168;
export const VENDOR_BRIEF_EXPANDED_STORAGE_PREFIX =
  "marketplace:orders:vendorBriefExpanded";
export const VENDOR_BRIEF_GROUPS_STORAGE_PREFIX =
  "marketplace:orders:vendorBriefGroups";

export const ORDER_PANEL_BODY_SECTIONS = [
  "scripts",
  "vendorBrief",
  "activity",
] as const;

export type BriefValueKind = "url" | "hex" | "font" | "short" | "long";
export type BriefFilter = "all" | "edited";
export type BriefCollapsibleGroup = "rules" | "brandKit";

const PINNED_BRIEF_FIELD: HandoffSnapshotFieldKey = "orderInstructions";

export interface VendorBriefSummary {
  fieldCount: number;
  editedCount: number;
  listEditedCount: number;
  allEdited: boolean;
}

export interface BriefGroupState {
  rules: boolean;
  brandKit: boolean;
}

export interface BriefDiffPair {
  orgDefault: string;
  orderValue: string;
  differs: boolean;
}

const DEFAULT_GROUP_STATE: BriefGroupState = { rules: true, brandKit: true };

export function summarizeVendorBrief(
  fields: readonly HandoffSnapshotFieldView[],
): VendorBriefSummary {
  const fieldCount = fields.length;
  const edited = fields.filter((row) => row.field.source === "order_override");
  const editedCount = edited.length;
  return {
    fieldCount,
    editedCount,
    listEditedCount: edited.filter((row) => row.key !== PINNED_BRIEF_FIELD).length,
    allEdited: fieldCount > 0 && editedCount === fieldCount,
  };
}

export function withoutPinnedBriefFields(
  fields: readonly HandoffSnapshotFieldView[],
): HandoffSnapshotFieldView[] {
  return fields.filter((row) => row.key !== PINNED_BRIEF_FIELD);
}

export function shouldShowOverrideRails(summary: VendorBriefSummary): boolean {
  return summary.editedCount > 0 && !summary.allEdited;
}

export function vendorBriefIsRecorded(
  snapshot: MarketplaceOrderHandoffSnapshot | null | undefined,
): boolean {
  if (snapshot?.recorded !== true) return false;
  return (
    handoffSnapshotHasVisibleFields(snapshot) || snapshot.thumbnailGuidelines != null
  );
}

type OrderLineAsset = {
  customAssetKind?: "SCRIPT" | "BRIEF" | null;
  briefId?: string | null;
};

/** True when the order’s line items are briefs (persona or imported), not scripts. */
export function orderUsesBriefLabels(
  scripts?: ReadonlyArray<OrderLineAsset>,
): boolean {
  return Boolean(
    scripts?.some(
      (script) => script.customAssetKind === "BRIEF" || Boolean(script.briefId),
    ),
  );
}

/** Hide the empty “Partner brief” CTA on script orders — that copy is for imported briefs. */
export function shouldShowVendorBriefSection(
  snapshot: MarketplaceOrderHandoffSnapshot | null | undefined,
  scripts?: ReadonlyArray<OrderLineAsset>,
): boolean {
  if (vendorBriefIsRecorded(snapshot)) return true;
  if (scripts === undefined) return true;
  return orderUsesBriefLabels(scripts);
}

export function filterBriefFields(
  fields: readonly HandoffSnapshotFieldView[],
  filter: BriefFilter,
): HandoffSnapshotFieldView[] {
  if (filter === "all") return [...fields];
  return fields.filter((row) => row.field.source === "order_override");
}

export function partitionBriefFields(
  fields: readonly HandoffSnapshotFieldView[],
): {
  perOrder: HandoffSnapshotFieldView[];
  rules: HandoffSnapshotFieldView[];
  brandKit: HandoffSnapshotFieldView[];
} {
  return {
    perOrder: fields.filter(
      (row) => row.section === "instructions" || row.section === "productAccuracy",
    ),
    rules: fields.filter((row) => row.section === "rules"),
    brandKit: fields.filter((row) => row.section === "brandKit"),
  };
}

export function classifyBriefValue(
  key: HandoffSnapshotFieldKey,
  value: string,
  folderLink?: string,
): BriefValueKind {
  if (folderLink || looksLikeHandoffUrl(value)) return "url";
  if (key === "coloursHex" && extractHexColors(value).length > 0) return "hex";
  if (key === "fontsLicenceProof" && extractFontNames(value).length > 0) {
    return "font";
  }
  const trimmed = value.trim();
  if (trimmed.length > 0 && trimmed.length < VENDOR_BRIEF_SHORT_VALUE_MAX && !trimmed.includes("\n")) {
    return "short";
  }
  return "long";
}

export function orgDefaultValueForField(
  key: HandoffSnapshotFieldKey,
  defaults: MarketplaceOrderHandoffDefaults | null | undefined,
): string {
  if (!defaults || key === "orderInstructions") return "";
  return defaults[key].trim();
}

export function briefDiffPair(
  orderValue: string,
  orgDefault: string,
): BriefDiffPair {
  const trimmedOrder = orderValue.trim();
  const trimmedDefault = orgDefault.trim();
  return {
    orgDefault: trimmedDefault,
    orderValue: trimmedOrder,
    differs: trimmedDefault.length > 0 && trimmedDefault !== trimmedOrder,
  };
}

export function vendorBriefExpandedStorageKey(userId: string): string {
  return `${VENDOR_BRIEF_EXPANDED_STORAGE_PREFIX}:${userId}`;
}

export function vendorBriefGroupsStorageKey(userId: string): string {
  return `${VENDOR_BRIEF_GROUPS_STORAGE_PREFIX}:${userId}`;
}

export function readVendorBriefExpanded(userId: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(vendorBriefExpandedStorageKey(userId)) === "1";
  } catch {
    return false;
  }
}

export function writeVendorBriefExpanded(userId: string, expanded: boolean): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      vendorBriefExpandedStorageKey(userId),
      expanded ? "1" : "0",
    );
  } catch {
    // Private browsing can block localStorage; in-memory state still works.
  }
}

export function readVendorBriefGroups(userId: string): BriefGroupState {
  if (typeof window === "undefined") return DEFAULT_GROUP_STATE;
  try {
    const raw = window.localStorage.getItem(vendorBriefGroupsStorageKey(userId));
    if (!raw) return DEFAULT_GROUP_STATE;
    const parsed = JSON.parse(raw) as Partial<BriefGroupState>;
    return {
      rules: parsed.rules !== false,
      brandKit: parsed.brandKit !== false,
    };
  } catch {
    return DEFAULT_GROUP_STATE;
  }
}

export function writeVendorBriefGroups(
  userId: string,
  state: BriefGroupState,
): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      vendorBriefGroupsStorageKey(userId),
      JSON.stringify(state),
    );
  } catch {
    // A persistence miss must not block collapsing in memory.
  }
}
